import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Briefcase, FileText, CalendarCheck, CalendarDays, Bell, History, UserCircle, LogOut, Menu, X, HardHat, Shield, MessageSquare } from "lucide-react";
import { api, clearToken } from "../lib/api";
import { Toaster } from "../components/ui/sonner";
import { TrialBanner, TrialExpiredBanner, TrialMobilePopup, usePlanStatus } from "./PlanGate";

const BASE_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/permits", label: "Permits", icon: FileText, testId: "nav-permits" },
  { to: "/inspections", label: "Inspections", icon: CalendarCheck, testId: "nav-inspections" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testId: "nav-calendar" },
  { to: "/jobs/new", label: "Add Job", icon: Briefcase, testId: "nav-add-job" },
  { to: "/alerts/settings", label: "Alert Settings", icon: Bell, testId: "nav-alerts" },
  { to: "/alerts/history", label: "Alert History", icon: History, testId: "nav-alert-history" },
  { to: "/feedback", label: "Feedback", icon: MessageSquare, testId: "nav-feedback" },
  { to: "/account", label: "Account", icon: UserCircle, testId: "nav-account" },
];

const FEEDBACK_URL = "https://docs.google.com/forms/d/e/1FAIpQLSd6NNSi4soAldOjnBDcecbZsc_vGzP4TfgxwY-k_88sLLbh6g/viewform?usp=publish-editor";

function FeedbackBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("feedback_banner_dismissed") === "true"; }
    catch { return false; }
  });

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem("feedback_banner_dismissed", "true"); } catch {}
  };

  if (dismissed) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-8 py-2.5" data-testid="feedback-banner">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
        <p className="text-sm text-zinc-800">
          Have feedback?{" "}
          <a
            href={FEEDBACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline underline-offset-2 hover:text-zinc-950"
            data-testid="feedback-banner-link"
          >
            Let us know here!
          </a>
        </p>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss feedback banner"
          className="shrink-0 text-zinc-500 hover:text-zinc-950 p-1"
          data-testid="feedback-banner-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

export const Layout = ({ user, outletUser }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { status: planStatus } = usePlanStatus();

  const isAdmin = Boolean(user?.is_admin);

  const NAV = isAdmin
    ? [...BASE_NAV, { to: "/admin", label: "Admin Panel", icon: Shield, testId: "nav-admin", adminOnly: true }]
    : BASE_NAV;

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearToken();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#FDFDFD]" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-zinc-950 text-zinc-200 transform ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transition-transform duration-150 flex flex-col`}
        data-testid="sidebar"
      >
        <Link to="/dashboard" data-testid="sidebar-brand-link" className="px-5 py-5 border-b border-zinc-800 flex items-center gap-2 hover:bg-zinc-900 transition-colors">
          <div className="w-7 h-7 bg-amber-500 rounded-sm flex items-center justify-center">
            <HardHat size={16} strokeWidth={2} className="text-zinc-950" />
          </div>
          <div>
            <div className="font-display font-bold text-white tracking-tight text-sm leading-none">Builder's Binder</div>
            <div className="label-eyebrow text-zinc-500 mt-1" style={{color:"#71717a"}}>Build Smarter</div>
          </div>
        </Link>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, testId, adminOnly }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                  adminOnly
                    ? isActive
                      ? "bg-purple-600 text-white font-semibold"
                      : "text-purple-300 hover:bg-purple-900/40 border border-purple-800/40 mt-2"
                    : isActive
                      ? "bg-amber-500 text-zinc-950 font-semibold"
                      : "text-zinc-300 hover:bg-zinc-900"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.75} />
              {label}
              {adminOnly && (
                <span className="ml-auto text-[9px] font-bold uppercase tracking-wider bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded-sm">
                  Admin
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-800 p-3">
          {user && (
            <div className="px-2 py-2 mb-2">
              <div className="flex items-center gap-1.5">
                <div className="text-xs text-zinc-500 label-eyebrow" style={{color:"#71717a"}}>SIGNED IN</div>
                {isAdmin && (
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded-sm">Admin</span>
                )}
              </div>
              <div className="text-sm text-white truncate mt-1" data-testid="sidebar-user-email">{user.email}</div>
            </div>
          )}
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-zinc-300 hover:bg-zinc-900"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-zinc-200 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3">
            <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div className="font-display font-bold tracking-tight">Builder's Binder</div>
            <div className="w-6" />
          </div>
          {/* Banners are hidden on mobile via hidden sm:block CSS in PlanGate */}
        </header>
        {/* Desktop-only banners — hidden sm:block prevents showing on mobile */}
        <FeedbackBanner />
        {!isAdmin && <TrialBanner status={planStatus} />}
        {!isAdmin && <TrialExpiredBanner status={planStatus} />}
        <main className="flex-1 overflow-x-hidden">
          <Outlet context={{ user: outletUser }} />
        </main>
      </div>

      {open && <div onClick={() => setOpen(false)} className="fixed inset-0 bg-black/40 z-30 lg:hidden" />}
      {!isAdmin && <TrialMobilePopup status={planStatus} />}
      <Toaster position="top-right" />
    </div>
  );
};
